import { useState } from "react";
import { Check, ArrowRight, Loader2, X, Lock, Camera, CalendarClock, Award, Gift } from "lucide-react";
import { toast } from "sonner";
import { PageHero, SectionLabel } from "@/components/Section";
import { SPONSOR_TIERS, TRIBUTE_TIER, IMAGES, CONTACT, PAYMENTS, SPONSOR_DETAILS_VISIBLE, SPONSOR_DEADLINES, IN_KIND_NOTE } from "@/data/content";
import { api } from "@/lib/api";
import { WhySponsor } from "@/components/WhySponsor";
import { PaymentMethod } from "@/components/PaymentMethod";
import { SpotsBadge } from "@/components/SpotsBadge";
import { TierSpotlight } from "@/components/TierSpotlight";
import { useSponsorConfig, remainingFor } from "@/hooks/useSponsorConfig";

const inputCls =
  "w-full border-b-2 border-border bg-transparent px-1 py-2.5 text-espresso rounded-none focus:outline-none focus:border-rust transition-colors placeholder:text-muted-foreground/60";

// Blurs sponsorship pricing/details while SPONSOR_DETAILS_VISIBLE is false.
const LOCK_CLS = SPONSOR_DETAILS_VISIBLE ? "" : "blur-[6px] select-none pointer-events-none";

export default function Sponsor() {
  const cfg = useSponsorConfig();
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState(PAYMENTS.sponsorship ? "card" : "check");
  const payByCard = PAYMENTS.sponsorship && method === "card";
  const [form, setForm] = useState({ contact_name: "", company: "", email: "", phone: "", notes: "" });
  const [notify, setNotify] = useState({ name: "", email: "", company: "" });
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    if (!notify.email) {
      toast.error("Please enter your email.");
      return;
    }
    setNotifyLoading(true);
    try {
      const { data } = await api.post("/submit/sponsor-notify", notify);
      toast.success(data.message || "You're on the list!");
      setNotifyDone(true);
      setNotify({ name: "", email: "", company: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setNotifyLoading(false);
    }
  };

  const legacy = SPONSOR_TIERS[0];
  const otherTiers = SPONSOR_TIERS.slice(1);
  const activations = cfg.activations || [];
  const soldOut = (key) => (remainingFor(cfg, key) ?? 1) <= 0;
  const activationRemaining = (a) => a.remaining ?? 1;

  const openTier = (tier) => {
    if (tier.soldOut) return;
    setForm({ contact_name: "", company: "", email: "", phone: "", notes: "" });
    setMethod(PAYMENTS.sponsorship ? "card" : "check");
    setActive(tier);
  };
  const openActivation = (a) => openTier({ key: a.key, name: a.label, price: a.amount, activation: true, soldOut: activationRemaining(a) <= 0 });

  const submitErr = (err, fallback) => {
    const d = err?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : fallback);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.company || !form.email || !form.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!payByCard) {
      setLoading(true);
      try {
        const { data } = await api.post("/submit/sponsorship", {
          tier: active.key,
          contact_name: form.contact_name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          notes: form.notes,
        });
        toast.success(data.message || "Sponsorship commitment received! We'll reach out shortly.");
        setActive(null);
      } catch (err) {
        submitErr(err, "Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/checkout/sponsorship", {
        tier: active.key,
        contact_name: form.contact_name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      submitErr(err, "Unable to start checkout. Please try again.");
    }
  };

  return (
    <div data-testid="sponsor-page">
      <PageHero
        overline="Join The Mission"
        title="Sponsorship"
        accent="Program"
        subtitle="Lead the way for Montgomery County's first responders. Our sponsorship program is built around leadership, community impact, and lasting recognition — designed to grow into a long-term partnership."
        image={IMAGES.texasSunset}
      />

      <WhySponsor />

      <section id="sponsorship-tiers" className="paper-noise bg-background scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 relative z-10">
          <SectionLabel>Six Ways To Partner</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl text-espresso">Choose Your Level</h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">Each level reflects leadership, community impact, and recognition — with benefits crafted to build a lasting corporate partnership with the Wayne Walker Foundation.</p>

          {/* Deadlines */}
          <div data-testid="sponsor-deadlines" className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="border-2 border-rust bg-espresso text-cream p-5 sm:p-6 flex gap-4 hard-shadow-sm">
              <span className="h-11 w-11 bg-rust text-primary-foreground flex items-center justify-center shrink-0"><Award size={20} /></span>
              <div>
                <div className="label-font text-[11px] text-rust tracking-[0.15em]">Founding Partner Deadline · {SPONSOR_DEADLINES.foundingDate}</div>
                <p className="mt-2 text-sm text-cream/85 leading-relaxed">{SPONSOR_DEADLINES.foundingText}</p>
              </div>
            </div>
            <div className="border border-border bg-card text-espresso p-5 sm:p-6 flex gap-4 hard-shadow-sm">
              <span className="h-11 w-11 bg-espresso text-cream flex items-center justify-center shrink-0"><CalendarClock size={20} /></span>
              <div>
                <div className="label-font text-[11px] text-rust tracking-[0.15em]">Commitment Deadline · {SPONSOR_DEADLINES.commitDate}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{SPONSOR_DEADLINES.commitText}</p>
              </div>
            </div>
          </div>

          {!SPONSOR_DETAILS_VISIBLE && (
            <div data-testid="sponsor-coming-soon" className="mt-8 border border-rust/40 bg-rust/5 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <Lock size={18} className="text-rust shrink-0 mt-0.5" />
                <p className="text-sm text-espresso">
                  <span className="font-semibold">Pricing &amp; full benefit details are being finalized.</span>{" "}
                  Want first access? Join the list below and we'll email you the complete sponsorship package the moment it's ready.
                </p>
              </div>
              {notifyDone ? (
                <p data-testid="sponsor-notify-success" className="mt-4 text-sm text-rust font-medium pl-7">
                  ✓ You're on the list — we'll be in touch soon.
                </p>
              ) : (
                <form onSubmit={submitNotify} className="mt-4 pl-0 sm:pl-7 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end" data-testid="sponsor-notify-form">
                  <div>
                    <label className="label-font text-[10px] text-espresso/70">Name</label>
                    <input data-testid="sponsor-notify-name" className={inputCls} value={notify.name} onChange={(e) => setNotify({ ...notify, name: e.target.value })} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="label-font text-[10px] text-espresso/70">Company</label>
                    <input data-testid="sponsor-notify-company" className={inputCls} value={notify.company} onChange={(e) => setNotify({ ...notify, company: e.target.value })} placeholder="Company (optional)" />
                  </div>
                  <div>
                    <label className="label-font text-[10px] text-espresso/70">Email *</label>
                    <input data-testid="sponsor-notify-email" type="email" className={inputCls} value={notify.email} onChange={(e) => setNotify({ ...notify, email: e.target.value })} placeholder="you@company.com" />
                  </div>
                  <button
                    data-testid="sponsor-notify-btn"
                    type="submit"
                    disabled={notifyLoading}
                    className="label-font text-xs bg-rust text-primary-foreground px-5 py-3 border border-espresso flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 hard-shadow-sm whitespace-nowrap"
                  >
                    {notifyLoading ? <Loader2 size={15} className="animate-spin" /> : "Notify Me"}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Featured Legacy Sponsor */}
          <div
            data-testid={`sponsor-tier-${legacy.key}`}
            className="mt-12 border border-rust bg-espresso text-cream hard-shadow-rust p-8 md:p-10"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="label-font text-[11px] text-rust">Legacy Sponsor · Presenting</div>
                  <SpotsBadge remaining={remainingFor(cfg, "legacy")} dark testId="spots-legacy" />
                </div>
                <div className={`mt-2 font-serif font-black text-5xl ${LOCK_CLS}`}>${legacy.price.toLocaleString()}</div>
                <div className={`mt-2 label-font text-[11px] text-cream/70 ${LOCK_CLS}`}>{legacy.availability}</div>
                <p className="mt-3 max-w-md text-cream/80 text-sm italic">{legacy.theme}</p>
                <TierSpotlight spotlight={cfg.spotlights?.legacy} dark testId="spotlight-legacy" />
              </div>
              <button
                data-testid={`sponsor-select-${legacy.key}`}
                onClick={() => openTier({ ...legacy, soldOut: soldOut("legacy") })}
                disabled={soldOut("legacy")}
                className="label-font text-xs bg-rust text-primary-foreground px-7 py-4 border border-cream/20 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 hard-shadow-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {soldOut("legacy") ? "Sold Out" : <>Become the Legacy Sponsor <ArrowRight size={15} /></>}
              </button>
            </div>
            <ul className={`mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-cream/85 ${LOCK_CLS}`}>
              {legacy.perks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="text-rust shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Other tiers */}
          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            {otherTiers.map((t) => (
              <div
                key={t.key}
                data-testid={`sponsor-tier-${t.key}`}
                className={`relative flex flex-col text-espresso hard-shadow-sm p-8 transition-transform hover:-translate-y-1 ${t.featured ? "border-2 border-rust bg-rust/10" : "border border-border bg-card"}`}
              >
                {t.featured && (
                  <span data-testid={`tier-badge-${t.key}`} className="absolute -top-3 left-6 label-font text-[10px] tracking-[0.15em] bg-rust text-primary-foreground px-3 py-1 border border-espresso">
                    {t.badge}
                  </span>
                )}
                <div className="flex items-baseline justify-between gap-3">
                  <div className="label-font text-sm text-rust">{t.name}</div>
                  <div className={`font-serif font-black text-3xl ${LOCK_CLS}`}>${t.price.toLocaleString()}</div>
                </div>
                <div className="mt-2"><SpotsBadge remaining={remainingFor(cfg, t.key)} testId={`spots-${t.key}`} /></div>
                <p className="mt-3 text-xs text-muted-foreground italic">{t.theme}</p>
                <ul className={`mt-5 space-y-2.5 flex-1 text-muted-foreground ${LOCK_CLS}`}>
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={`shrink-0 mt-0.5 ${t.featured ? "text-rust" : "text-espresso/60"}`} /> {p}
                    </li>
                  ))}
                </ul>
                <TierSpotlight spotlight={cfg.spotlights?.[t.key]} testId={`spotlight-${t.key}`} />
                <button
                  data-testid={`sponsor-select-${t.key}`}
                  onClick={() => openTier({ ...t, soldOut: soldOut(t.key) })}
                  disabled={soldOut(t.key)}
                  className={`mt-7 label-font text-xs px-6 py-3.5 border border-espresso flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed ${t.featured ? "bg-rust text-primary-foreground hard-shadow-sm" : "bg-transparent text-espresso hover:bg-espresso hover:text-cream"}`}
                >
                  {soldOut(t.key) ? "Sold Out" : <>Sponsor This Tier <ArrowRight size={15} /></>}
                </button>
              </div>
            ))}

            {/* Sponsor a Hero Team — full-width under the five tiers */}
            <div
              data-testid="sponsor-tier-tribute"
              className="sm:col-span-2 flex flex-col border border-border bg-card text-espresso hard-shadow-sm p-8 transition-transform hover:-translate-y-1"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="label-font text-sm text-rust">{TRIBUTE_TIER.name}</div>
                <div className={`font-serif font-black text-3xl ${LOCK_CLS}`}>${TRIBUTE_TIER.price.toLocaleString()}</div>
              </div>
              <div className="mt-2"><SpotsBadge remaining={remainingFor(cfg, "tribute")} testId="spots-tribute" /></div>
              <p className="mt-3 text-xs text-muted-foreground italic">{TRIBUTE_TIER.theme}</p>
              <div className="mt-5 grid md:grid-cols-2 gap-x-10 gap-y-5 flex-1">
                <ul className={`space-y-2.5 text-muted-foreground ${LOCK_CLS}`}>
                  {TRIBUTE_TIER.gridHighlights.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-espresso/60 shrink-0 mt-0.5" /> {p}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col">
                  <div data-testid="tribute-photo-op" className="flex items-start gap-3 border border-border bg-background/60 p-3.5">
                    <Camera size={16} className="text-espresso/60 shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground leading-relaxed">{TRIBUTE_TIER.photoOp}</p>
                  </div>
                  <TierSpotlight spotlight={cfg.spotlights?.tribute} testId="spotlight-tribute" />
                  <button
                    data-testid="sponsor-select-tribute"
                    onClick={() => openTier({ ...TRIBUTE_TIER, soldOut: soldOut("tribute") })}
                    disabled={soldOut("tribute")}
                    className="mt-5 label-font text-xs bg-transparent text-espresso px-6 py-3.5 border border-espresso flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:bg-espresso hover:text-cream disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {soldOut("tribute") ? "Sold Out" : <>Sponsor a Hero Team <ArrowRight size={15} /></>}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Activation sponsorships */}
          <div id="activations" data-testid="sponsor-activations" className="mt-20 scroll-mt-24">
            <SectionLabel>À La Carte</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-3xl sm:text-4xl text-espresso">Activation Sponsorships</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">Own a signature moment of the day. Each activation is exclusive — one sponsor, maximum visibility — and can be combined with any level above.</p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {activations.map((a) => {
                const left = activationRemaining(a);
                return (
                  <div key={a.key} data-testid={`activation-${a.key}`} className="flex flex-col border border-border bg-background p-6 hard-shadow-sm transition-transform hover:-translate-y-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-serif font-bold text-lg text-espresso leading-tight">{a.label}</div>
                      <div className="font-serif font-black text-2xl text-rust whitespace-nowrap">${Number(a.amount).toLocaleString()}</div>
                    </div>
                    <div className="mt-2"><SpotsBadge remaining={left} testId={`spots-${a.key}`} /></div>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{a.desc}</p>
                    <button
                      data-testid={`activation-select-${a.key}`}
                      onClick={() => openActivation(a)}
                      disabled={left <= 0}
                      className="mt-5 label-font text-[11px] border border-espresso px-5 py-3 text-espresso flex items-center justify-center gap-2 hover:bg-espresso hover:text-cream transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {left <= 0 ? "Claimed" : <>Claim This Activation <ArrowRight size={14} /></>}
                    </button>
                  </div>
                );
              })}
            </div>
            <div data-testid="in-kind-note" className="mt-6 flex items-start gap-3 border border-rust/40 bg-rust/5 p-4 max-w-3xl">
              <Gift size={18} className="text-rust shrink-0 mt-0.5" />
              <p className="text-sm text-espresso">{IN_KIND_NOTE}</p>
            </div>
          </div>

          <p className="mt-10 text-sm text-muted-foreground">
            Prefer to talk it through? Contact <span className="text-rust font-medium">{CONTACT.sponsorEmail}</span> · {CONTACT.sponsorPhone}
          </p>
        </div>
      </section>

      {/* Tribute is presented as a standard tier card in the grid above */}

      {/* Sponsorship Theme */}
      <section className="bg-espresso text-cream">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 md:py-24">
          <SectionLabel>The Meaning Behind The Levels</SectionLabel>
          <h2 className="mt-5 font-serif font-black text-4xl">Sponsorship Theme</h2>
          <p className="mt-4 max-w-2xl text-cream/75">
            The sponsorship levels represent the values that define both Wayne Walker's legacy and the men and women who serve our communities every day.
          </p>
          <div className="mt-10 border-t border-cream/15 divide-y divide-cream/15">
            {[...SPONSOR_TIERS, TRIBUTE_TIER].map((t) => (
              <div key={t.key} className="py-5 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6">
                <div className="label-font text-sm text-rust sm:w-36 shrink-0">{t.name}</div>
                <p className="text-cream/85">{t.theme}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {active && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-espresso/70 backdrop-blur-sm" onClick={() => !loading && setActive(null)}>
          <div
            data-testid="sponsor-modal"
            className="w-full max-w-lg bg-background border border-espresso hard-shadow max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border p-6">
              <div>
                <div className="label-font text-xs text-rust">{active.activation ? "Activation Sponsorship" : active.key === "tribute" ? active.name : `${active.name} Sponsor`}</div>
                <div className={`font-serif font-black text-3xl text-espresso ${LOCK_CLS}`}>${active.price.toLocaleString()}</div>
                {active.activation && <div className="mt-1 text-xs text-muted-foreground">{active.name}</div>}
              </div>
              <button data-testid="sponsor-modal-close" onClick={() => !loading && setActive(null)} className="text-espresso hover:text-rust"><X size={22} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-5">
              <div>
                <label className="label-font text-[11px] text-espresso/70">Contact Name *</label>
                <input data-testid="sponsor-name" className={inputCls} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Your full name" />
              </div>
              <div>
                <label className="label-font text-[11px] text-espresso/70">Company / Organization *</label>
                <input data-testid="sponsor-company" className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Email *</label>
                  <input data-testid="sponsor-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
                </div>
                <div>
                  <label className="label-font text-[11px] text-espresso/70">Phone *</label>
                  <input data-testid="sponsor-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(713) 555-0100" />
                </div>
              </div>
              <div>
                <label className="label-font text-[11px] text-espresso/70">Notes (optional)</label>
                <textarea data-testid="sponsor-notes" rows={3} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={active.activation ? "Logo needs, in-kind contribution details, etc." : "Logo needs, special requests, etc."} />
              </div>
              {PAYMENTS.sponsorship && <PaymentMethod value={method} onChange={setMethod} prefix="sponsor" compact />}
              <button
                data-testid="sponsor-checkout-btn"
                type="submit"
                disabled={loading}
                className="w-full label-font text-sm bg-rust text-primary-foreground px-6 py-4 border border-espresso flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 hard-shadow-sm"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> {payByCard ? "Redirecting…" : "Submitting…"}</> : payByCard ? <>Proceed to Payment — ${active.price.toLocaleString()}</> : <>Submit Commitment — {active.name}</>}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                {payByCard
                  ? "Secure checkout powered by Stripe."
                  : PAYMENTS.sponsorship
                    ? "We'll email an invoice with check / ACH instructions to finalize your sponsorship."
                    : "Online payments coming soon — submit and we'll follow up to finalize your sponsorship."}
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
